import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Tooltip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ResponsiveContainer,
  Treemap,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
} from 'recharts';
import { useLocation, useNavigate } from 'react-router-dom';

const CRYPTO_LIMIT = 100;
const TOP_METRIC_ASSET_COUNT = 10;
const MAX_COMPARE_COINS = 10;
const FINANCIAL_FONT_STACK = '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif';
const FINANCIAL_NUMERIC_FONT_STACK = '"Inter", "SF Pro Text", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif';
const COMPARE_SERIES_COLORS = [
  '#22c55e',
  '#3b82f6',
  '#f59e0b',
  '#ef4444',
  '#06b6d4',
  '#a855f7',
  '#f97316',
  '#ec4899',
  '#84cc16',
  '#14b8a6',
];
const COMPARE_METRIC_OPTIONS = [
  { key: 'price', label: 'Price', source: 'prices' },
  { key: 'marketCap', label: 'Market Cap', source: 'market_caps' },
  { key: 'volume', label: 'Trading Volume', source: 'total_volumes' },
  { key: 'return', label: 'Percentage Return', source: 'prices' },
  { key: 'activeAddresses', label: 'Active Addresses', source: 'AdrActCnt' },
  { key: 'transactions', label: 'Transaction Count', source: 'TxCnt' },
  { key: 'networkFeesUsd', label: 'Network Fees (USD)', source: 'FeeTotNtv' },
];
const COMPARE_RANGE_OPTIONS = [
  { days: 7, label: '7D' },
  { days: 30, label: '30D' },
  { days: 90, label: '90D' },
  { days: 365, label: '1Y' },
  { days: 1825, label: '5Y' },
  { days: 'max', label: 'Max' },
];
const PRICE_RANGE_OPTIONS = [
  ...COMPARE_RANGE_OPTIONS.filter((option) => Number(option.days) <= 365),
  { days: 1825, label: '5Y' },
  { days: 3650, label: '10Y' },
];

const toUtcDate = (value) => new Date(value).toISOString().slice(0, 10);

const waitForRetry = (delayMs, signal) => new Promise((resolve, reject) => {
  const handleAbort = () => {
    window.clearTimeout(timeoutId);
    reject(new DOMException('Aborted', 'AbortError'));
  };
  const timeoutId = window.setTimeout(() => {
    signal?.removeEventListener('abort', handleAbort);
    resolve();
  }, delayMs);

  if (signal?.aborted) {
    handleAbort();
    return;
  }
  signal?.addEventListener('abort', handleAbort, { once: true });
});

const fetchJsonWithRetry = async (url, signal, errorLabel, maxAttempts = 3) => {
  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const response = await fetch(url, { signal });
      if (response.ok) return response.json();

      const error = new Error(`${errorLabel} (${response.status})`);
      error.retryable = response.status === 429 || response.status >= 500;
      if (!error.retryable) throw error;
      lastError = error;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      if (error.retryable === false) throw error;
      lastError = error;
    }

    if (attempt < maxAttempts) {
      await waitForRetry(attempt * 300, signal);
    }
  }

  throw lastError || new Error(errorLabel);
};

const settleWithConcurrency = async (items, concurrency, worker) => {
  const results = new Array(items.length);
  let nextIndex = 0;

  const runWorker = async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      try {
        results[currentIndex] = { status: 'fulfilled', value: await worker(items[currentIndex]) };
      } catch (reason) {
        results[currentIndex] = { status: 'rejected', reason };
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, runWorker));
  return results;
};

const fetchMarketHistory = async (coinId, days, signal, symbol = '', marketCapAsset = '') => {
  const params = new URLSearchParams({ days: String(days) });
  if (symbol) params.set('symbol', symbol);
  if (marketCapAsset) params.set('marketCapAsset', marketCapAsset);
  return fetchJsonWithRetry(
    `/api/crypto/market-history/${encodeURIComponent(coinId)}?${params.toString()}`,
    signal,
    'Market history failed'
  );
};

export const CryptoPriceChart = ({ coin, isDarkMode, height = '100%' }) => {
  const [rangeDays, setRangeDays] = useState(30);
  const [points, setPoints] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState('');
  const [chartSource, setChartSource] = useState('CoinGecko');

  useEffect(() => {
    if (!coin?.id) return undefined;

    const controller = new AbortController();
    const loadHistory = async () => {
      setChartLoading(true);
      setChartError('');
      try {
        const payload = await fetchMarketHistory(coin.id, rangeDays, controller.signal, coin.symbol);
        const nextPoints = (Array.isArray(payload?.prices) ? payload.prices : [])
          .map(([timestamp, price]) => ({ timestamp, price: Number(price) }))
          .filter((point) => Number.isFinite(point.timestamp) && Number.isFinite(point.price));
        if (!nextPoints.length) throw new Error('The provider returned no price history');
        setPoints(nextPoints);
        setChartSource(payload.source || (rangeDays > 365 ? 'Yahoo Finance' : 'CoinGecko'));
      } catch (error) {
        if (error.name !== 'AbortError') {
          setPoints([]);
          setChartError('Price history is temporarily unavailable.');
        }
      } finally {
        if (!controller.signal.aborted) setChartLoading(false);
      }
    };

    loadHistory();
    return () => controller.abort();
  }, [coin?.id, coin?.symbol, rangeDays]);

  const formatPrice = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: Math.abs(value) >= 100000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(value) < 1 ? 6 : 2,
  }).format(value);

  return (
    <Box sx={{ height, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {chartSource} market price (USD)
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={rangeDays}
          onChange={(_event, nextRange) => {
            if (nextRange) setRangeDays(nextRange);
          }}
          aria-label="Price chart range"
        >
          {PRICE_RANGE_OPTIONS.map((option) => (
            <ToggleButton key={option.days} value={option.days} aria-label={option.label}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {chartLoading ? (
          <CircularProgress size={32} />
        ) : chartError ? (
          <Alert severity="warning" sx={{ width: '100%' }}>{chartError}</Alert>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 12, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e5e7eb'} />
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(value) => new Date(value).toLocaleDateString(
                  'en-US',
                  rangeDays > 365 ? { month: 'short', year: 'numeric' } : { month: 'short', day: 'numeric' }
                )}
                minTickGap={32}
              />
              <YAxis dataKey="price" domain={['auto', 'auto']} tickFormatter={formatPrice} width={82} />
              <RechartsTooltip
                labelFormatter={(value) => new Date(value).toLocaleString()}
                formatter={(value) => [formatPrice(value), 'Price']}
                contentStyle={{
                  backgroundColor: isDarkMode ? '#111827' : '#ffffff',
                  borderColor: isDarkMode ? '#475569' : '#d1d5db',
                }}
              />
              <Line type="monotone" dataKey="price" stroke="#16a34a" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

const fetchCoinMetricsHistory = async (assets, metric, days, signal) => {
  const params = new URLSearchParams({
    assets,
    metrics: metric,
    days: String(days),
  });
  const payload = await fetchJsonWithRetry(
    `/api/crypto/on-chain-history?${params.toString()}`,
    signal,
    'On-chain history failed'
  );
  return Array.isArray(payload?.data) ? payload.data : [];
};

// Rank the tracked coins by a given metric and return the top `count` ids.
const getTopCoinsByMetric = (coins, metricKey, count) => {
  return [...coins]
    .filter((coin) => Number.isFinite(coin?.[metricKey]))
    .sort((a, b) => (b[metricKey] || 0) - (a[metricKey] || 0))
    .slice(0, count)
    .map((coin) => coin.id);
};

// Fetch Solana on-chain metrics from backend proxy (avoids CORS issues)
const fetchSolanaMetrics = async () => {
  try {
    const response = await fetch('/api/solana-metrics');
    if (!response.ok) {
      console.warn('Backend Solana metrics fetch failed:', response.status);
      return null;
    }
    const data = await response.json();
    console.log('Solana metrics from backend:', data);
    return data;
  } catch (error) {
    console.warn('Failed to fetch Solana metrics:', error);
    return null;
  }
};

// Map CoinGecko IDs to Coin Metrics asset IDs that have daily on-chain data.
// Only include assets that are verified to have AdrActCnt/TxCnt/FeeTotNtv metrics.
const COIN_METRICS_ASSET_MAP = {
  // Layer 1 chains with on-chain data
  bitcoin: 'btc',
  ethereum: 'eth',
  solana: 'sol', // Uses Solana public RPC for metrics
  cardano: 'ada',
  ripple: 'xrp',
  polkadot: 'dot',
  avalanche: 'avaxc',  // Use C-Chain (has metrics)
  dogecoin: 'doge',
  litecoin: 'ltc',
  'bitcoin-cash': 'bch',
  stellar: 'xlm',
  'ethereum-classic': 'etc',
  tron: 'trx',
  algorand: 'algo',
  tezos: 'xtz',
  eos: 'eos',
  flow: 'flow_native',  // Use flow_native (has metrics)
  'internet-computer': 'icp',
  neo: 'neo',
  dash: 'dash',
  zcash: 'zec',
  binancecoin: 'bnb',
  'crypto-com-chain': 'cro',
  digibyte: 'dgb',
  vertcoin: 'vtc',
  'bitcoin-gold': 'btg',
  'bitcoin-sv': 'bsv',
  decred: 'dcr',
  
  // Stablecoins with on-chain data
  tether: 'usdt_eth',
  'usd-coin': 'usdc',
  dai: 'dai',
  'true-usd': 'tusd',
  'binance-usd': 'busd',
  'pax-dollar': 'pax',
  'paxos-gold': 'paxg',
  'gemini-dollar': 'gusd',
  frax: 'frax_eth',
  'liquity-usd': 'lusd_eth',
  'paypal-usd': 'pyusd_eth',
  'ethena-usde': 'usde_eth',
  
  // DeFi & tokens with on-chain data  
  chainlink: 'link',
  uniswap: 'uni',
  'shiba-inu': 'shib_eth',
  'matic-network': 'matic_eth',
  aave: 'aave',
  maker: 'mkr',
  'compound-governance-token': 'comp',
  'curve-dao-token': 'crv',
  sushi: 'sushi',
  'yearn-finance': 'yfi',
  'synthetix-network-token': 'snx',
  'basic-attention-token': 'bat',
  decentraland: 'mana',
  'lido-dao': 'ldo',
  balancer: 'bal',
  '0x': 'zrx',
  'kyber-network-crystal': 'knc',
  'republic-protocol': 'ren',
  'loopring': 'lrc_eth',
  omisego: 'omg',
  'status': 'snt',
  'power-ledger': 'powr',
  'augur': 'rep',
  gnosis: 'gno',
  quant: 'qnt',
  perpetual: 'perp',
  alpha: 'alpha',
  'uma': 'uma',
  'serum': 'srm',
  'wrapped-bitcoin': 'wbtc',
  'lido-staked-ether': 'steth_lido',
  'nexus-mutual': 'nxm',
  
  // Exchange tokens with on-chain data
  'ftx-token': 'ftt',
  'kucoin-shares': 'kcs',
  'leo-token': 'leo_eth',
  'huobi-token': 'ht',
  
  // Note: These popular coins do NOT have daily metrics in Coin Metrics community API:
  // solana (sol), near, cosmos (atom), fantom (ftm), hedera (hbar), filecoin (fil),
  // theta, aptos (apt), arbitrum (arb), optimism (op), sui, ton
};

const TREEMAP_METRIC_OPTIONS = [
  { key: 'marketCap', label: 'Market Cap' },
  { key: 'volume24h', label: '24h Volume' },
  { key: 'price', label: 'Price' },
  { key: 'priceChange24h', label: '24h Change' },
  { key: 'activeAddresses', label: 'Active Addresses' },
  { key: 'transactionCount24h', label: 'Transactions' },
  { key: 'networkFeesUsd', label: 'Network Fees (USD)' },
];

const CryptoAnalysis = ({ view = 'dashboard' }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDarkMode = theme.palette.mode === 'dark';
  const [cryptoData, setCryptoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTreemapIds, setSelectedTreemapIds] = useState([]);
  const [treemapMetric, setTreemapMetric] = useState('priceChange24h');
  const [selectedPriceCoinId, setSelectedPriceCoinId] = useState(null);
  const [priceChartSearch, setPriceChartSearch] = useState('');
  const [coinsSearch, setCoinsSearch] = useState('');
  const [coinDescriptions, setCoinDescriptions] = useState({});
  const [descriptionLoadingId, setDescriptionLoadingId] = useState(null);
  const [marketSortBy, setMarketSortBy] = useState('rank');
  const [marketSortDirection, setMarketSortDirection] = useState('asc');
  const [selectedCompareIds, setSelectedCompareIds] = useState(() => {
    const requestedIds = new URLSearchParams(location.search).get('coins');
    return requestedIds ? requestedIds.split(',').filter(Boolean).slice(0, MAX_COMPARE_COINS) : [];
  });
  const [compareMetric, setCompareMetric] = useState(() => {
    const requestedMetric = new URLSearchParams(location.search).get('metric');
    return COMPARE_METRIC_OPTIONS.some((option) => option.key === requestedMetric) ? requestedMetric : 'price';
  });
  const [compareRangeDays, setCompareRangeDays] = useState(() => {
    const requestedRange = new URLSearchParams(location.search).get('range');
    const parsedRange = requestedRange === 'max' ? 'max' : Number(requestedRange);
    return COMPARE_RANGE_OPTIONS.some((option) => option.days === parsedRange) ? parsedRange : 30;
  });
  const [compareMode, setCompareMode] = useState(() => (
    new URLSearchParams(location.search).get('mode') === 'absolute' ? 'absolute' : 'normalized'
  ));
  const [compareSeries, setCompareSeries] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState('');

  useEffect(() => {
    fetchCryptoData();
  }, []);

  // Initial/refresh default: keep the user's current picks if still valid,
  // otherwise fall back to the top N assets for the active metric.
  useEffect(() => {
    if (!cryptoData.length) return;

    setSelectedTreemapIds((prev) => {
      const availableIds = new Set(cryptoData.map((coin) => coin.id));
      const filteredPrev = prev.filter((id) => availableIds.has(id));
      if (filteredPrev.length) return filteredPrev;

      return getTopCoinsByMetric(cryptoData, treemapMetric, TOP_METRIC_ASSET_COUNT);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptoData]);

  useEffect(() => {
    if (view !== 'compare' || selectedCompareIds.length < 2) return;
    const params = new URLSearchParams({
      coins: selectedCompareIds.join(','),
      metric: compareMetric,
      range: String(compareRangeDays),
      mode: compareMode,
    });
    const nextSearch = params.toString();
    if (location.search.slice(1) !== nextSearch) {
      navigate({ pathname: '/crypto-analysis/compare', search: nextSearch }, { replace: true });
    }
  }, [view, selectedCompareIds, compareMetric, compareRangeDays, compareMode, location.search, navigate]);

  useEffect(() => {
    if (!cryptoData.length) return;
    setSelectedCompareIds((previousIds) => {
      const availableIds = new Set(cryptoData.map((coin) => coin.id));
      const validPreviousIds = previousIds.filter((id) => availableIds.has(id)).slice(0, MAX_COMPARE_COINS);
      if (validPreviousIds.length >= 2) return validPreviousIds;

      const preferredIds = ['bitcoin', 'ethereum', 'solana'].filter((id) => availableIds.has(id));
      return preferredIds.length >= 2 ? preferredIds : cryptoData.slice(0, 3).map((coin) => coin.id);
    });
  }, [cryptoData]);

  // Switching the "Color by" metric re-populates the treemap with the top 10
  // (of the 100 tracked) assets ranked by that metric.
  useEffect(() => {
    if (!cryptoData.length) return;
    setSelectedTreemapIds(getTopCoinsByMetric(cryptoData, treemapMetric, TOP_METRIC_ASSET_COUNT));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treemapMetric]);

  useEffect(() => {
    if (!cryptoData.length) return;
    const requestedCoinId = new URLSearchParams(location.search).get('coin');

    setSelectedPriceCoinId((prev) => {
      if (requestedCoinId && cryptoData.some((coin) => coin.id === requestedCoinId)) {
        return requestedCoinId;
      }
      if (prev && cryptoData.some((coin) => coin.id === prev)) return prev;
      return cryptoData[0].id;
    });
  }, [cryptoData, location.search]);

  useEffect(() => {
    if (!selectedPriceCoinId) return;
    if (Object.prototype.hasOwnProperty.call(coinDescriptions, selectedPriceCoinId)) return;

    let isCancelled = false;

    const loadDescription = async () => {
      setDescriptionLoadingId(selectedPriceCoinId);
      const description = await fetchCoinDescription(selectedPriceCoinId);
      if (isCancelled) return;

      setCoinDescriptions((prev) => ({ ...prev, [selectedPriceCoinId]: description }));
      setDescriptionLoadingId((prev) => (prev === selectedPriceCoinId ? null : prev));
    };

    loadDescription();

    return () => {
      isCancelled = true;
    };
  }, [selectedPriceCoinId, coinDescriptions]);

  useEffect(() => {
    if (view !== 'compare' || selectedCompareIds.length < 2) {
      setCompareSeries([]);
      return undefined;
    }

    const controller = new AbortController();
    const selectedCoins = cryptoData.filter((coin) => selectedCompareIds.includes(coin.id));
    const metricOption = COMPARE_METRIC_OPTIONS.find((option) => option.key === compareMetric);

    const loadComparison = async () => {
      setCompareLoading(true);
      setCompareError('');

      const usesMarketHistory = ['price', 'marketCap', 'volume', 'return'].includes(compareMetric);
      const results = await settleWithConcurrency(selectedCoins, 2, async (coin) => {
        if (usesMarketHistory) {
          const marketHistory = await fetchMarketHistory(
            coin.id,
            compareRangeDays,
            controller.signal,
            coin.symbol,
            compareMetric === 'marketCap' ? coin.coinMetricsAsset : ''
          );
          const rows = Array.isArray(marketHistory?.[metricOption.source]) ? marketHistory[metricOption.source] : [];
          return {
            coin,
            points: rows.map(([timestamp, value]) => ({ date: toUtcDate(timestamp), value: Number(value) })),
          };
        }

        if (!coin.coinMetricsAsset) {
          throw new Error('On-chain history is unavailable');
        }

        const needsPriceHistory = compareMetric === 'networkFeesUsd';
        const [metricRows, marketHistory] = await Promise.all([
          fetchCoinMetricsHistory(coin.coinMetricsAsset, metricOption.source, compareRangeDays, controller.signal),
          needsPriceHistory
            ? fetchMarketHistory(coin.id, compareRangeDays, controller.signal, coin.symbol)
            : Promise.resolve(null),
        ]);
        const pricesByDate = new Map(
          (marketHistory?.prices || []).map(([timestamp, value]) => [toUtcDate(timestamp), Number(value)])
        );
        return {
          coin,
          points: metricRows.map((row) => {
            const date = toUtcDate(row.time);
            const nativeValue = Number(row[metricOption.source]);
            const price = pricesByDate.get(date);
            return {
              date,
              value: Number.isFinite(nativeValue) && (!needsPriceHistory || Number.isFinite(price))
                ? (needsPriceHistory ? nativeValue * price : nativeValue)
                : null,
            };
          }),
        };
      });

      if (controller.signal.aborted) return;
      const successfulSeries = results
        .filter((result) => result.status === 'fulfilled' && result.value.points.some((point) => Number.isFinite(point.value)))
        .map((result) => result.value);
      setCompareSeries(successfulSeries);

      const failedCount = results.length - successfulSeries.length;
      if (!successfulSeries.length) {
        setCompareError('Historical data is unavailable for the selected assets and metric.');
      } else if (failedCount) {
        setCompareError(`${failedCount} selected asset${failedCount === 1 ? '' : 's'} could not be loaded for this metric.`);
      }
      setCompareLoading(false);
    };

    loadComparison().catch((loadError) => {
      if (loadError.name === 'AbortError') return;
      setCompareSeries([]);
      setCompareError(loadError.message || 'Unable to load historical comparison data.');
      setCompareLoading(false);
    });

    return () => controller.abort();
  }, [view, cryptoData, selectedCompareIds, compareMetric, compareRangeDays]);

  const resolveCoinMetricsAsset = (coin) => {
    if (!coin) return null;
    const mapped = COIN_METRICS_ASSET_MAP[coin.id];
    if (mapped) return mapped;
    return String(coin.symbol || '').toLowerCase() || null;
  };

  const fetchCryptoData = async () => {
    try {
      setLoading(true);
      setError(null);

      const filteredData = await fetchTopMarketData();

      const onChainMetricsByAsset = await fetchOnChainMetrics(filteredData);

      const enrichedData = filteredData.map((coin) => {
        const assetKey = resolveCoinMetricsAsset(coin);
        const onChainMetrics = onChainMetricsByAsset[assetKey] || {};
        
        // Only use real on-chain data - no estimates
        const activeAddresses = onChainMetrics.activeAddresses ?? null;
        const transactionCount24h = onChainMetrics.transactionCount24h ?? null;
        const networkFeesNative = onChainMetrics.networkFeesNative ?? null;
        
        // Use direct USD fees if available (e.g., from DefiLlama), otherwise calculate from native
        let networkFeesUsd = onChainMetrics.networkFeesUsd ?? null;
        if (networkFeesUsd === null && networkFeesNative !== null) {
          networkFeesUsd = networkFeesNative * (coin.price || 0);
        }
        
        return {
          ...coin,
          coinMetricsAsset: assetKey,
          transactionCount24h,
          activeAddresses,
          networkFeesNative,
          networkFeesUsd,
        };
      });

      setCryptoData(enrichedData);
    } catch (err) {
      console.error('Error fetching crypto data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopMarketData = async () => {
    const response = await fetch(`/api/crypto/markets?limit=${CRYPTO_LIMIT}`);
    if (!response.ok) {
      throw new Error(`Crypto market request failed (${response.status})`);
    }
    const rows = await response.json();
    return Array.isArray(rows) ? rows : [];
  };

  const fetchCoinDescription = async (coinId) => {
    try {
      const response = await fetch(
        `https://api.coingecko.com/api/v3/coins/${coinId}?localization=false&tickers=false&market_data=false&community_data=false&developer_data=false&sparkline=false`
      );
      if (!response.ok) return '';

      const data = await response.json();
      const englishDescription = data?.description?.en || '';
      return englishDescription.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    } catch (error) {
      console.warn(`Failed to fetch description for ${coinId}:`, error);
      return '';
    }
  };

  const fetchOnChainMetrics = async (coins) => {
    try {
      const mappedAssets = [...new Set(coins.map((coin) => resolveCoinMetricsAsset(coin)).filter(Boolean))];
      if (!mappedAssets.length) return {};

      const catalogResp = await fetch(
        '/api/crypto/on-chain/catalog'
      );
      if (!catalogResp.ok) {
        throw new Error(`Coin Metrics catalog fetch failed: ${catalogResp.statusText}`);
      }

      const catalogJson = await catalogResp.json();
      const metricRows = Array.isArray(catalogJson.data) ? catalogJson.data : [];
      const activeMetric = metricRows.find((row) => row.metric === 'AdrActCnt');
      const feeMetric = metricRows.find((row) => row.metric === 'FeeTotNtv');
      const txMetric = metricRows.find((row) => row.metric === 'TxCnt');

      const getSupportedAssets = (metricRow) => {
        if (!metricRow?.frequencies) return new Set();
        const daily = metricRow.frequencies.find((f) => f.frequency === '1d');
        return new Set((daily?.assets || []).map((asset) => asset.toLowerCase()));
      };

      const activeSupportedAssets = getSupportedAssets(activeMetric);
      const feeSupportedAssets = getSupportedAssets(feeMetric);
      const txSupportedAssets = getSupportedAssets(txMetric);

      const activeAssetsToFetch = mappedAssets.filter((asset) => activeSupportedAssets.has(asset));
      const feeAssetsToFetch = mappedAssets.filter((asset) => feeSupportedAssets.has(asset));
      const txAssetsToFetch = mappedAssets.filter((asset) => txSupportedAssets.has(asset));

      const fetchMetricRows = async (metricName, assets) => {
        if (!assets.length) return [];

        const params = new URLSearchParams({
          assets: assets.join(','),
          metrics: metricName,
          days: '3',
        });

        const resp = await fetch(`/api/crypto/on-chain-history?${params.toString()}`);
        if (!resp.ok) {
          console.warn(`Coin Metrics ${metricName} fetch failed:`, resp.statusText);
          return [];
        }

        const json = await resp.json();
        return Array.isArray(json.data) ? json.data : [];
      };

      const [activeRows, feeRows, txRows] = await Promise.all([
        fetchMetricRows('AdrActCnt', activeAssetsToFetch),
        fetchMetricRows('FeeTotNtv', feeAssetsToFetch),
        fetchMetricRows('TxCnt', txAssetsToFetch),
      ]);

      const resultByAsset = {};
      const latestByAsset = (rows, field) => {
        const latest = {};
        rows.forEach((row) => {
          const asset = String(row.asset || '').toLowerCase();
          if (!asset || row[field] === undefined || row[field] === null) return;
          const rowTime = new Date(row.time).getTime();
          if (!latest[asset] || rowTime > latest[asset].time) {
            latest[asset] = { value: Number(row[field]), time: rowTime };
          }
        });
        return latest;
      };

      const activeLatest = latestByAsset(activeRows, 'AdrActCnt');
      const feeLatest = latestByAsset(feeRows, 'FeeTotNtv');
      const txLatest = latestByAsset(txRows, 'TxCnt');

      mappedAssets.forEach((asset) => {
        resultByAsset[asset] = {
          transactionCount24h: Number.isFinite(txLatest[asset]?.value) ? txLatest[asset].value : null,
          activeAddresses: Number.isFinite(activeLatest[asset]?.value) ? activeLatest[asset].value : null,
          networkFeesNative: Number.isFinite(feeLatest[asset]?.value) ? feeLatest[asset].value : null,
        };
      });

      // Fetch Solana metrics from RPC + DefiLlama (not available in Coin Metrics free tier)
      const solanaMetrics = await fetchSolanaMetrics();
      if (solanaMetrics) {
        resultByAsset['sol'] = solanaMetrics;
        console.log('Solana metrics merged:', solanaMetrics);
      }

      return resultByAsset;
    } catch (error) {
      console.warn('Unable to enrich on-chain metrics:', error);
      return {};
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatCompactCurrencySig4 = (value) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';

    const absValue = Math.abs(value);
    if (absValue < 1000000) {
      return formatCurrency(value);
    }

    const units = [
      { threshold: 1e12, suffix: 'T' },
      { threshold: 1e9, suffix: 'B' },
      { threshold: 1e6, suffix: 'M' },
    ];

    for (const unit of units) {
      if (absValue >= unit.threshold) {
        const scaled = value / unit.threshold;
        const absScaled = Math.abs(scaled);
        const integerDigits = absScaled >= 1 ? Math.floor(Math.log10(absScaled)) + 1 : 1;
        const decimals = Math.max(0, 4 - integerDigits);
        return `$${scaled.toFixed(decimals)}${unit.suffix}`;
      }
    }

    return formatCurrency(value);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
    }).format(value);
  };

  const formatInteger = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  const formatNativeFee = (value, symbol) => {
    if (value === null || value === undefined) return 'N/A';
    if (Math.abs(value) >= 1) return `${value.toFixed(2)} ${symbol}`;
    return `${value.toFixed(6)} ${symbol}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const color = value >= 0 ? '#10b981' : '#ef4444';
    const icon = value >= 0 ? '▲' : '▼';
    return (
      <Box sx={{ color, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon} {Math.abs(value).toFixed(2)}%
      </Box>
    );
  };

  const marketSortableValue = (coin, column) => {
    if (!coin) return null;

    switch (column) {
      case 'rank':
        return Number.isFinite(coin.rank) ? coin.rank : Number.POSITIVE_INFINITY;
      case 'name':
        return String(coin.name || '').toLowerCase();
      case 'symbol':
        return String(coin.symbol || '').toLowerCase();
      case 'coinMetricsAsset':
        return String(coin.coinMetricsAsset || '').toLowerCase();
      case 'price':
        return Number.isFinite(coin.price) ? coin.price : null;
      case 'marketCap':
        return Number.isFinite(coin.marketCap) ? coin.marketCap : null;
      case 'volume24h':
        return Number.isFinite(coin.volume24h) ? coin.volume24h : null;
      case 'activeAddresses':
        return Number.isFinite(coin.activeAddresses) ? coin.activeAddresses : null;
      case 'transactionCount24h':
        return Number.isFinite(coin.transactionCount24h) ? coin.transactionCount24h : null;
      case 'networkFeesNative':
        return Number.isFinite(coin.networkFeesNative) ? coin.networkFeesNative : null;
      case 'circulatingSupply':
        return Number.isFinite(coin.circulatingSupply) ? coin.circulatingSupply : null;
      case 'totalSupply':
        return Number.isFinite(coin.totalSupply) ? coin.totalSupply : null;
      case 'priceChange24h':
        return Number.isFinite(coin.priceChange24h) ? coin.priceChange24h : null;
      default:
        return null;
    }
  };

  const sortedMarketData = useMemo(() => {
    const rows = [...cryptoData];
    rows.sort((a, b) => {
      const av = marketSortableValue(a, marketSortBy);
      const bv = marketSortableValue(b, marketSortBy);

      if (av === null || av === undefined || av === '') {
        return bv === null || bv === undefined || bv === '' ? 0 : 1;
      }
      if (bv === null || bv === undefined || bv === '') {
        return -1;
      }

      let compare;
      if (typeof av === 'string' || typeof bv === 'string') {
        compare = String(av).localeCompare(String(bv));
      } else {
        compare = av === bv ? 0 : (av > bv ? 1 : -1);
      }

      return marketSortDirection === 'asc' ? compare : -compare;
    });
    return rows;
  }, [cryptoData, marketSortBy, marketSortDirection]);

  const handleMarketSort = (column) => {
    if (marketSortBy === column) {
      setMarketSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    const defaultDirection = (column === 'name' || column === 'symbol' || column === 'coinMetricsAsset' || column === 'rank')
      ? 'asc'
      : 'desc';

    setMarketSortBy(column);
    setMarketSortDirection(defaultDirection);
  };

  const getTreemapMetricValue = (coin, metricKey) => {
    const rawValue = coin?.[metricKey];
    return Number.isFinite(rawValue) ? rawValue : 0;
  };

  const getRawMetricValue = (coin, metricKey) => {
    return coin?.[metricKey] ?? null;
  };

  const getTreemapMetricDisplay = (value, metricKey, rawValue = null) => {
    // Show N/A if the original value was null (rawValue passed explicitly)
    if (rawValue === null && !Number.isFinite(value)) {
      return 'N/A';
    }
    if (metricKey === 'priceChange24h') {
      return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
    }
    if (metricKey === 'activeAddresses' || metricKey === 'transactionCount24h') {
      // Show N/A if value is 0 but rawValue wasn't provided (legacy calls) 
      // or if rawValue is explicitly null
      if (value === 0 && (rawValue === null || rawValue === undefined)) {
        return 'N/A';
      }
      return formatInteger(value);
    }
    return formatCompactCurrencySig4(value);
  };

  const getTreemapCellColor = (normalized, metricKey) => {
    if (metricKey === 'priceChange24h') {
      if (normalized <= 0.5) {
        const intensity = 1 - normalized * 2;
        return `hsl(0, ${70 + intensity * 15}%, ${18 + intensity * 14}%)`;
      }
      const intensity = (normalized - 0.5) * 2;
      return `hsl(140, ${55 + intensity * 20}%, ${18 + intensity * 18}%)`;
    }

    const hue = 200 - normalized * 70;
    const saturation = 55 + normalized * 20;
    const lightness = 20 + normalized * 15;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const openCoinDetails = (coinId) => {
    if (!coinId) return;
    navigate(`/crypto-analysis/coins?coin=${encodeURIComponent(coinId)}`);
  };

  const handleTreemapKeyDown = (event, coinId) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openCoinDetails(coinId);
  };

  const buildTreemapData = () => {
    const selectedCoins = cryptoData.filter((coin) => selectedTreemapIds.includes(coin.id));
    if (!selectedCoins.length) return [];

    const metricValues = selectedCoins.map((coin) => getTreemapMetricValue(coin, treemapMetric));
    const minMetric = Math.min(...metricValues);
    const maxMetric = Math.max(...metricValues);
    const metricRange = maxMetric - minMetric;

    return selectedCoins.map((coin) => {
      const sizeValue = Math.max(coin.marketCap || coin.volume24h || 1, 1);
      const metricValue = getTreemapMetricValue(coin, treemapMetric);
      const rawValue = getRawMetricValue(coin, treemapMetric);
      const normalized = metricRange === 0 ? 0.5 : (metricValue - minMetric) / metricRange;

      return {
        id: coin.id,
        name: coin.symbol,
        fullName: coin.name,
        size: sizeValue,
        metricValue,
        rawValue,
        displayValue: getTreemapMetricDisplay(metricValue, treemapMetric, rawValue),
        bgColor: getTreemapCellColor(normalized, treemapMetric),
      };
    });
  };

  const treemapBarData = useMemo(() => {
    return cryptoData
      .filter((coin) => selectedTreemapIds.includes(coin.id))
      .map((coin) => {
        const metricValue = getTreemapMetricValue(coin, treemapMetric);
        const rawValue = getRawMetricValue(coin, treemapMetric);
        return {
          id: coin.id,
          symbol: coin.symbol,
          metricValue,
          rawValue,
          displayValue: getTreemapMetricDisplay(metricValue, treemapMetric, rawValue),
        };
      })
      .sort((a, b) => (b.metricValue || 0) - (a.metricValue || 0))
      .slice(0, 15);
  }, [cryptoData, selectedTreemapIds, treemapMetric]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading cryptocurrency data...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading cryptocurrency data: {error}
      </Alert>
    );
  }

  const paginatedData = sortedMarketData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const totalMarketCap = cryptoData.reduce((sum, coin) => sum + (coin.marketCap || 0), 0);
  const totalVolume24h = cryptoData.reduce((sum, coin) => sum + (coin.volume24h || 0), 0);
  const avgPriceChange24h = cryptoData.length
    ? cryptoData.reduce((sum, coin) => sum + (coin.priceChange24h || 0), 0) / cryptoData.length
    : 0;
  const gainersCount = cryptoData.filter((coin) => (coin.priceChange24h || 0) > 0).length;
  const losersCount = cryptoData.filter((coin) => (coin.priceChange24h || 0) < 0).length;

  const validVolumeRatioCoins = cryptoData.filter((coin) => (coin.marketCap || 0) > 0);
  const avgVolumeToMarketCap = validVolumeRatioCoins.length
    ? validVolumeRatioCoins.reduce((sum, coin) => sum + ((coin.volume24h || 0) / coin.marketCap), 0) / validVolumeRatioCoins.length
    : 0;

  const topMovers = [...cryptoData]
    .filter((coin) => coin.priceChange24h !== null && coin.priceChange24h !== undefined)
    .sort((a, b) => (b.priceChange24h || 0) - (a.priceChange24h || 0))
    .slice(0, 5);

  const highestVolumeCoin = [...cryptoData]
    .filter((coin) => coin.volume24h)
    .sort((a, b) => (b.volume24h || 0) - (a.volume24h || 0))[0];

  const highestMarketCapCoin = [...cryptoData]
    .filter((coin) => coin.marketCap)
    .sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0))[0];
  const onChainCoverageCount = cryptoData.filter(
    (coin) => coin.activeAddresses !== null || coin.networkFeesNative !== null
  ).length;
  const selectedTreemapCoins = cryptoData.filter((coin) => selectedTreemapIds.includes(coin.id));
  const currentTreemapMetricLabel =
    TREEMAP_METRIC_OPTIONS.find((option) => option.key === treemapMetric)?.label || 'Market Cap';
  const selectedPriceCoin = cryptoData.find((coin) => coin.id === selectedPriceCoinId) || null;
  const selectedCoinDescription = selectedPriceCoinId ? coinDescriptions[selectedPriceCoinId] : '';
  const isDescriptionLoading = descriptionLoadingId === selectedPriceCoinId && !selectedCoinDescription;
  const selectedCompareCoins = cryptoData.filter((coin) => selectedCompareIds.includes(coin.id));
  const currentCompareMetric = COMPARE_METRIC_OPTIONS.find((option) => option.key === compareMetric) || COMPARE_METRIC_OPTIONS[0];
  const comparisonRows = (() => {
    const rowsByDate = new Map();
    compareSeries.forEach(({ coin, points }) => {
      const finitePoints = points.filter((point) => Number.isFinite(point.value));
      const firstValue = finitePoints[0]?.value;
      finitePoints.forEach((point) => {
        const row = rowsByDate.get(point.date) || { date: point.date };
        const normalizedValue = firstValue ? (point.value / firstValue) * 100 : null;
        row[coin.id] = compareMetric === 'return'
          ? (firstValue ? ((point.value / firstValue) - 1) * 100 : null)
          : compareMode === 'normalized'
            ? normalizedValue
            : point.value;
        rowsByDate.set(point.date, row);
      });
    });
    return [...rowsByDate.values()].sort((left, right) => left.date.localeCompare(right.date));
  })();
  const comparisonSummary = compareSeries.map(({ coin, points }) => {
    const finitePoints = points.filter((point) => Number.isFinite(point.value));
    const startValue = finitePoints[0]?.value ?? null;
    const latestValue = finitePoints[finitePoints.length - 1]?.value ?? null;
    return {
      coin,
      startValue,
      latestValue,
      change: startValue ? ((latestValue / startValue) - 1) * 100 : null,
    };
  });
  const formatCompareValue = (value, metric = compareMetric) => {
    if (!Number.isFinite(value)) return 'N/A';
    if (metric === 'return') return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
    if (metric === 'activeAddresses' || metric === 'transactions') return formatNumber(value);
    return formatCompactCurrencySig4(value);
  };
  const formatTreemapMarketCap = (val) => {
    if (val === null || val === undefined || !Number.isFinite(val)) return 'N/A';
    return formatCompactCurrencySig4(val);
  };

  return (
    <Box
      sx={{
        p: 3,
        fontFamily: FINANCIAL_FONT_STACK,
        '& .MuiTypography-root': {
          fontFamily: FINANCIAL_FONT_STACK,
        },
      }}
    >
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        {view === 'dashboard'
          ? 'Crypto Dashboard'
          : view === 'compare'
            ? 'Compare Cryptocurrencies'
          : view === 'price'
            ? 'Crypto Price Chart'
            : view === 'coins'
              ? 'Cryptocurrency Details'
              : 'Top 100 Cryptocurrency Analysis'}
      </Typography>

      {/* Summary Cards */}
      {view !== 'price' && view !== 'coins' && <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tracked
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {cryptoData.length}
              </Typography>
              <Chip
                icon={<TrendingUpIcon />}
                label="Cryptocurrencies"
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Market Cap
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {formatCurrency(totalMarketCap)}
              </Typography>
              <Chip
                label={highestMarketCapCoin?.symbol ? `Top Weight: ${highestMarketCapCoin.symbol}` : 'Top Weight: N/A'}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                24h Volume
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {formatCurrency(totalVolume24h)}
              </Typography>
              <Chip
                label={highestVolumeCoin?.symbol ? `Top Volume: ${highestVolumeCoin.symbol}` : 'Top Volume: N/A'}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg 24h Change
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {avgPriceChange24h >= 0 ? '+' : ''}{avgPriceChange24h.toFixed(2)}%
              </Typography>
              <Chip
                icon={avgPriceChange24h >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                label={`${gainersCount} Gainers / ${losersCount} Losers`}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>}

      {view === 'dashboard' && (
        <>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                Liquidity & Participation
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Avg Volume / Market Cap: <strong>{(avgVolumeToMarketCap * 100).toFixed(2)}%</strong>
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Positive Momentum Assets: <strong>{gainersCount}</strong>
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Negative Momentum Assets: <strong>{losersCount}</strong>
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                On-chain Coverage: <strong>{onChainCoverageCount}/{cryptoData.length}</strong>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (actual data only)
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                These metrics help approximate market breadth, trading depth, and directional sentiment beyond raw transaction count.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2.5, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                Top 24h Movers
              </Typography>
              {topMovers.map((coin) => (
                <Box key={coin.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {coin.symbol}
                  </Typography>
                  {formatPercentage(coin.priceChange24h)}
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>

        {/* Dashboard Treemap */}
        <Paper sx={{ p: 2.5 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Market Cap Treemap
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Size = Market Cap. Color = selected metric. Pick assets to include in the treemap.
              </Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={cryptoData}
                value={selectedTreemapCoins}
                onChange={(_, value) => setSelectedTreemapIds(value.map((coin) => coin.id))}
                getOptionLabel={(option) => `${option.symbol} - ${option.name}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="Assets in treemap" placeholder="Select cryptocurrencies" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Color by
              </Typography>
              <FormControl fullWidth size="small">
                <Select value={treemapMetric} onChange={(event) => setTreemapMetric(event.target.value)}>
                  {TREEMAP_METRIC_OPTIONS.map((option) => (
                    <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <ResponsiveContainer width="100%" height={480}>
            <Treemap
              data={buildTreemapData()}
              dataKey="size"
              aspectRatio={16 / 9}
              isAnimationActive={false}
              content={(props) => {
                const { x, y, width, height, id, name, fullName, displayValue, bgColor } = props;
                if (!name || width < 4 || height < 4) return null;

                const showLabel = width > 42 && height > 26;
                const showValue = width > 64 && height > 42;
                const fontSize = Math.max(Math.min(width / 4.9, height / 2.9, 24), 11);
                const valueFontSize = Math.max(Math.min(fontSize * 0.72, 18), 10);
                const valueBadgeWidth = Math.max(Math.min(width * 0.8, 180), 56);
                const valueBadgeHeight = Math.max(Math.min(height * 0.32, 34), 16);
                const valueBadgeX = x + (width - valueBadgeWidth) / 2;
                const valueBadgeY = y + height / 2 + fontSize * 0.08;

                return (
                  <g
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${fullName || name} coin details`}
                    onClick={() => openCoinDetails(id)}
                    onKeyDown={(event) => handleTreemapKeyDown(event, id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <rect
                      x={x + 1}
                      y={y + 1}
                      width={Math.max(width - 2, 0)}
                      height={Math.max(height - 2, 0)}
                      style={{ fill: bgColor || '#1a1a2a', stroke: 'rgba(15, 23, 42, 0.9)', strokeWidth: 2 }}
                    />
                    {showLabel && (
                      <text
                        x={x + width / 2}
                        y={y + height / 2 - (showValue ? fontSize * 0.35 : 0)}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{
                          fill: 'rgba(245,247,250,0.94)',
                          fontSize,
                          fontWeight: 800,
                          fontFamily: FINANCIAL_FONT_STACK,
                          letterSpacing: 0.25,
                          textShadow: '0 1px 2px rgba(15, 23, 42, 0.85)',
                          pointerEvents: 'none',
                        }}
                      >
                        {name}
                      </text>
                    )}
                    {showValue && (
                      <>
                        <rect
                          x={valueBadgeX}
                          y={valueBadgeY}
                          width={valueBadgeWidth}
                          height={valueBadgeHeight}
                          rx={6}
                          fill="rgba(15, 23, 42, 0.42)"
                        />
                        <text
                          x={x + width / 2}
                          y={valueBadgeY + valueBadgeHeight / 2}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          style={{
                            fill: 'rgba(248,250,252,0.98)',
                            fontSize: valueFontSize,
                            fontWeight: 700,
                            fontFamily: FINANCIAL_NUMERIC_FONT_STACK,
                            letterSpacing: 0.15,
                            fontVariantNumeric: 'tabular-nums',
                            textShadow: '0 1px 2px rgba(15, 23, 42, 0.9)',
                            pointerEvents: 'none',
                          }}
                        >
                          {displayValue}
                        </text>
                      </>
                    )}
                  </g>
                );
              }}
            >
              <RechartsTooltip
                content={({ payload }) => {
                  if (!payload || !payload.length) return null;
                  const data = payload[0]?.payload;
                  if (!data) return null;
                  return (
                    <Box sx={{
                      bgcolor: 'rgba(20,20,30,0.95)',
                      border: '1px solid rgba(100,100,120,0.5)',
                      borderRadius: 1,
                      p: 1.5,
                      minWidth: 120,
                    }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#fff', fontFamily: FINANCIAL_FONT_STACK, letterSpacing: 0.15 }}>
                        {data.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'rgba(180,200,180,0.9)', mt: 0.5, fontFamily: FINANCIAL_NUMERIC_FONT_STACK, fontVariantNumeric: 'tabular-nums' }}>
                        Market Cap: {formatTreemapMarketCap(data.size)}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'rgba(180,200,220,0.9)', fontFamily: FINANCIAL_NUMERIC_FONT_STACK, fontVariantNumeric: 'tabular-nums' }}>
                        {currentTreemapMetricLabel}: {data.displayValue}
                      </Typography>
                    </Box>
                  );
                }}
              />
            </Treemap>
          </ResponsiveContainer>
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">Lowest</Typography>
            <Box
              sx={{
                flex: 1,
                maxWidth: 220,
                height: 10,
                borderRadius: 1,
                background:
                  treemapMetric === 'priceChange24h'
                    ? 'linear-gradient(to right, hsl(0,80%,20%), #111, hsl(140,70%,30%))'
                    : 'linear-gradient(to right, hsl(200,55%,20%), hsl(165,68%,32%))',
              }}
            />
            <Typography variant="caption" color="text.secondary">Highest</Typography>
          </Box>
        </Paper>

        <Paper sx={{ p: 2.5, mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Metric Bar Chart
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Same assets and metric as the treemap. Bars are ordered by value.
          </Typography>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={treemapBarData} margin={{ top: 8, right: 20, left: 28, bottom: 18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(148,163,184,0.18)' : 'rgba(100,116,139,0.2)'} />
              <XAxis dataKey="symbol" tick={{ fill: isDarkMode ? '#cbd5e1' : '#334155', fontSize: 12 }} />
              <YAxis
                width={108}
                tick={{ fill: isDarkMode ? '#cbd5e1' : '#334155', fontSize: 12 }}
                tickFormatter={(value) => getTreemapMetricDisplay(Number(value), treemapMetric)}
              />
              <RechartsTooltip
                formatter={(value, _name, item) => {
                  const raw = item?.payload?.rawValue;
                  return [getTreemapMetricDisplay(Number(value), treemapMetric, raw), currentTreemapMetricLabel];
                }}
              />
              <Bar dataKey="metricValue" radius={[6, 6, 0, 0]}>
                {treemapBarData.map((entry) => (
                  <Cell
                    key={`bar-cell-${entry.id}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${entry.symbol} coin details`}
                    onClick={() => openCoinDetails(entry.id)}
                    onKeyDown={(event) => handleTreemapKeyDown(event, entry.id)}
                    style={{ cursor: 'pointer' }}
                    fill={
                      treemapMetric === 'priceChange24h'
                        ? (entry.metricValue >= 0 ? '#10b981' : '#ef4444')
                        : '#3b82f6'
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
        </>
      )}

      {view === 'treemap' && (
        <Box>
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Crypto Treemap
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Rectangle size uses market cap. Color is driven by the selected metric. Pick any of the tracked assets below to include them in the treemap.
                </Typography>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={cryptoData}
                  value={selectedTreemapCoins}
                  onChange={(_, value) => setSelectedTreemapIds(value.map((coin) => coin.id))}
                  getOptionLabel={(option) => `${option.symbol} - ${option.name}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Assets in treemap" placeholder="Select cryptocurrencies" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Color by
                  </Typography>
                  <Select value={treemapMetric} onChange={(event) => setTreemapMetric(event.target.value)}>
                    {TREEMAP_METRIC_OPTIONS.map((option) => (
                      <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Size = Market Cap. Color = {currentTreemapMetricLabel}.
            </Typography>
            <ResponsiveContainer width="100%" height={680}>
              <Treemap
                data={buildTreemapData()}
                dataKey="size"
                aspectRatio={16 / 9}
                isAnimationActive={false}
                content={(props) => {
                  const { x, y, width, height, id, name, fullName, displayValue, bgColor } = props;
                  if (!name || width < 4 || height < 4) return null;

                  const showLabel = width > 46 && height > 28;
                  const showValue = width > 66 && height > 44;
                  const fontSize = Math.max(Math.min(width / 4.6, height / 2.7, 30), 11);
                  const valueFontSize = Math.max(Math.min(fontSize * 0.72, 21), 10);
                  const valueBadgeWidth = Math.max(Math.min(width * 0.82, 220), 64);
                  const valueBadgeHeight = Math.max(Math.min(height * 0.33, 38), 18);
                  const valueBadgeX = x + (width - valueBadgeWidth) / 2;
                  const valueBadgeY = y + height / 2 + fontSize * 0.08;

                  return (
                    <g
                      role="button"
                      tabIndex={0}
                      aria-label={`View ${fullName || name} coin details`}
                      onClick={() => openCoinDetails(id)}
                      onKeyDown={(event) => handleTreemapKeyDown(event, id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <rect
                        x={x + 1}
                        y={y + 1}
                        width={Math.max(width - 2, 0)}
                        height={Math.max(height - 2, 0)}
                        style={{ fill: bgColor || '#1a1a2a', stroke: 'rgba(15, 23, 42, 0.9)', strokeWidth: 2 }}
                      />
                      {showLabel && (
                        <text
                          x={x + width / 2}
                          y={y + height / 2 - (showValue ? fontSize * 0.35 : 0)}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          style={{
                            fill: 'rgba(245,247,250,0.94)',
                            fontSize,
                            fontWeight: 800,
                            fontFamily: FINANCIAL_FONT_STACK,
                            letterSpacing: 0.25,
                            textShadow: '0 1px 2px rgba(15, 23, 42, 0.85)',
                            pointerEvents: 'none',
                          }}
                        >
                          {name}
                        </text>
                      )}
                      {showValue && (
                        <>
                          <rect
                            x={valueBadgeX}
                            y={valueBadgeY}
                            width={valueBadgeWidth}
                            height={valueBadgeHeight}
                            rx={6}
                            fill="rgba(15, 23, 42, 0.42)"
                          />
                          <text
                            x={x + width / 2}
                            y={valueBadgeY + valueBadgeHeight / 2}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{
                              fill: 'rgba(248,250,252,0.98)',
                              fontSize: valueFontSize,
                              fontWeight: 700,
                              fontFamily: FINANCIAL_NUMERIC_FONT_STACK,
                              letterSpacing: 0.15,
                              fontVariantNumeric: 'tabular-nums',
                              textShadow: '0 1px 2px rgba(15, 23, 42, 0.9)',
                              pointerEvents: 'none',
                            }}
                          >
                            {displayValue}
                          </text>
                        </>
                      )}
                    </g>
                  );
                }}
              >
                <RechartsTooltip
                  content={({ payload }) => {
                    if (!payload || !payload.length) return null;
                    const data = payload[0]?.payload;
                    if (!data) return null;
                    return (
                      <Box sx={{
                        bgcolor: 'rgba(20,20,30,0.95)',
                        border: '1px solid rgba(100,100,120,0.5)',
                        borderRadius: 1,
                        p: 1.5,
                        minWidth: 120,
                      }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#fff', fontFamily: FINANCIAL_FONT_STACK, letterSpacing: 0.15 }}>
                          {data.name}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'rgba(180,200,180,0.9)', mt: 0.5, fontFamily: FINANCIAL_NUMERIC_FONT_STACK, fontVariantNumeric: 'tabular-nums' }}>
                          Market Cap: {formatTreemapMarketCap(data.size)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'rgba(180,200,220,0.9)', fontFamily: FINANCIAL_NUMERIC_FONT_STACK, fontVariantNumeric: 'tabular-nums' }}>
                          {currentTreemapMetricLabel}: {data.displayValue}
                        </Typography>
                      </Box>
                    );
                  }}
                />
              </Treemap>
            </ResponsiveContainer>
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">Lowest</Typography>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: 220,
                  height: 10,
                  borderRadius: 1,
                  background:
                    treemapMetric === 'priceChange24h'
                      ? 'linear-gradient(to right, hsl(0,80%,20%), #111, hsl(140,70%,30%))'
                      : 'linear-gradient(to right, hsl(200,55%,20%), hsl(165,68%,32%))',
                }}
              />
              <Typography variant="caption" color="text.secondary">Highest</Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {view === 'compare' && (
        <Box>
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} lg={5}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Assets (2-{MAX_COMPARE_COINS})
                </Typography>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={cryptoData}
                  value={selectedCompareCoins}
                  onChange={(_, value) => setSelectedCompareIds(value.slice(0, MAX_COMPARE_COINS).map((coin) => coin.id))}
                  getOptionLabel={(option) => `${option.symbol} - ${option.name}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} placeholder="Select cryptocurrencies" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Metric</Typography>
                <FormControl fullWidth size="small">
                  <Select value={compareMetric} onChange={(event) => setCompareMetric(event.target.value)}>
                    {COMPARE_METRIC_OPTIONS.map((option) => (
                      <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Range</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={compareRangeDays}
                  onChange={(_, value) => value && setCompareRangeDays(value)}
                  fullWidth
                >
                  {COMPARE_RANGE_OPTIONS.map((option) => (
                    <ToggleButton key={option.days} value={option.days}>{option.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
            </Grid>
            {compareMetric !== 'return' && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={compareMode}
                onChange={(_, value) => value && setCompareMode(value)}
                sx={{ mt: 2 }}
              >
                <ToggleButton value="normalized">Indexed to 100</ToggleButton>
                <ToggleButton value="absolute">Absolute values</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Paper>

          {selectedCompareIds.length < 2 ? (
            <Alert severity="info">Select at least two cryptocurrencies to compare.</Alert>
          ) : (
            <>
              {compareError && <Alert severity={compareSeries.length ? 'warning' : 'error'} sx={{ mb: 2 }}>{compareError}</Alert>}
              <Paper sx={{ p: 2.5, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {currentCompareMetric.label} over time
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {compareMetric === 'return'
                    ? 'Cumulative return from the first available observation.'
                    : compareMode === 'normalized'
                      ? 'Each series starts at 100 for direct relative comparison.'
                      : 'Actual reported values for each asset.'}
                </Typography>
                {compareLoading ? (
                  <Box sx={{ height: { xs: 480, md: 600, xl: 680 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : comparisonRows.length ? (
                  <Box sx={{ height: { xs: 480, md: 600, xl: 680 } }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={comparisonRows} margin={{ top: 8, right: 20, left: 12, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(148,163,184,0.18)' : 'rgba(100,116,139,0.2)'} />
                        <XAxis dataKey="date" minTickGap={32} tick={{ fill: isDarkMode ? '#cbd5e1' : '#334155', fontSize: 12 }} />
                        <YAxis
                          width={92}
                          tick={{ fill: isDarkMode ? '#cbd5e1' : '#334155', fontSize: 12 }}
                          tickFormatter={(value) => compareMetric === 'return'
                            ? `${Number(value).toFixed(0)}%`
                            : compareMode === 'normalized'
                              ? Number(value).toFixed(0)
                              : formatCompareValue(Number(value))}
                        />
                        <RechartsTooltip
                          content={({ active, label, payload }) => {
                            if (!active || !payload?.length) return null;
                            const sortedPayload = [...payload]
                              .filter((entry) => Number.isFinite(Number(entry.value)))
                              .sort((left, right) => Number(right.value) - Number(left.value));
                            if (!sortedPayload.length) return null;

                            return (
                              <Box
                                sx={{
                                  bgcolor: isDarkMode ? '#111827' : '#ffffff',
                                  border: '1px solid',
                                  borderColor: isDarkMode ? '#475569' : '#d1d5db',
                                  borderRadius: 1,
                                  boxShadow: 3,
                                  p: 1.25,
                                  minWidth: 130,
                                }}
                              >
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                  {label}
                                </Typography>
                                {sortedPayload.map((entry) => (
                                  <Box
                                    key={entry.dataKey}
                                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.2 }}
                                  >
                                    <Typography variant="caption" sx={{ color: entry.color, fontWeight: 600 }}>
                                      {entry.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: entry.color, fontVariantNumeric: 'tabular-nums' }}>
                                      {compareMetric === 'return'
                                        ? formatCompareValue(Number(entry.value), 'return')
                                        : compareMode === 'normalized'
                                          ? Number(entry.value).toFixed(2)
                                          : formatCompareValue(Number(entry.value))}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            );
                          }}
                        />
                        <Legend />
                        {compareSeries.map(({ coin }, index) => (
                          <Line
                            key={coin.id}
                            type="monotone"
                            dataKey={coin.id}
                            name={coin.symbol}
                            stroke={COMPARE_SERIES_COLORS[index % COMPARE_SERIES_COLORS.length]}
                            strokeWidth={2.5}
                            dot={false}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">No historical observations available.</Typography>
                  </Box>
                )}
              </Paper>

              {!!comparisonSummary.length && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Asset</TableCell>
                        <TableCell align="right">Start</TableCell>
                        <TableCell align="right">Latest</TableCell>
                        <TableCell align="right">Change</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparisonSummary.map(({ coin, startValue, latestValue, change }) => (
                        <TableRow key={coin.id}>
                          <TableCell sx={{ fontWeight: 600 }}>{coin.symbol} - {coin.name}</TableCell>
                          <TableCell align="right">
                            {compareMetric === 'return' ? '0.00%' : formatCompareValue(startValue)}
                          </TableCell>
                          <TableCell align="right">
                            {compareMetric === 'return' ? formatCompareValue(change, 'return') : formatCompareValue(latestValue)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: change >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                            {Number.isFinite(change) ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}%` : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>
      )}

      {view === 'market' && <TableContainer component={Paper}>
        <Table>
          <TableHead
            sx={{
              backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.16)' : '#f3f4f6',
              '& .MuiTableCell-root': {
                color: 'text.primary',
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={marketSortBy === 'rank'}
                  direction={marketSortBy === 'rank' ? marketSortDirection : 'asc'}
                  onClick={() => handleMarketSort('rank')}
                >
                  Rank
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={marketSortBy === 'name'}
                  direction={marketSortBy === 'name' ? marketSortDirection : 'asc'}
                  onClick={() => handleMarketSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={marketSortBy === 'symbol'}
                  direction={marketSortBy === 'symbol' ? marketSortDirection : 'asc'}
                  onClick={() => handleMarketSort('symbol')}
                >
                  Symbol
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={marketSortBy === 'coinMetricsAsset'}
                  direction={marketSortBy === 'coinMetricsAsset' ? marketSortDirection : 'asc'}
                  onClick={() => handleMarketSort('coinMetricsAsset')}
                >
                  On-chain Asset
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={marketSortBy === 'price'}
                  direction={marketSortBy === 'price' ? marketSortDirection : 'desc'}
                  onClick={() => handleMarketSort('price')}
                >
                  Price
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={marketSortBy === 'marketCap'}
                  direction={marketSortBy === 'marketCap' ? marketSortDirection : 'desc'}
                  onClick={() => handleMarketSort('marketCap')}
                >
                  Market Cap
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={marketSortBy === 'volume24h'}
                  direction={marketSortBy === 'volume24h' ? marketSortDirection : 'desc'}
                  onClick={() => handleMarketSort('volume24h')}
                >
                  24h Volume
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                  <TableSortLabel
                    active={marketSortBy === 'activeAddresses'}
                    direction={marketSortBy === 'activeAddresses' ? marketSortDirection : 'desc'}
                    onClick={() => handleMarketSort('activeAddresses')}
                  >
                    Active Addresses (24h)
                  </TableSortLabel>
                  <Tooltip title="Daily unique active addresses from Coin Metrics metric AdrActCnt for the mapped on-chain asset.">
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                  <TableSortLabel
                    active={marketSortBy === 'transactionCount24h'}
                    direction={marketSortBy === 'transactionCount24h' ? marketSortDirection : 'desc'}
                    onClick={() => handleMarketSort('transactionCount24h')}
                  >
                    Transactions (24h)
                  </TableSortLabel>
                  <Tooltip title="Daily transaction count from Coin Metrics metric TxCnt for the mapped on-chain asset.">
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                  <TableSortLabel
                    active={marketSortBy === 'networkFeesNative'}
                    direction={marketSortBy === 'networkFeesNative' ? marketSortDirection : 'desc'}
                    onClick={() => handleMarketSort('networkFeesNative')}
                  >
                    Network Fees (24h)
                  </TableSortLabel>
                  <Tooltip title="Daily total fees in native units from Coin Metrics metric FeeTotNtv for the mapped on-chain asset.">
                    <IconButton size="small" sx={{ p: 0.25 }}>
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={marketSortBy === 'circulatingSupply'}
                  direction={marketSortBy === 'circulatingSupply' ? marketSortDirection : 'desc'}
                  onClick={() => handleMarketSort('circulatingSupply')}
                >
                  Circulating Supply
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={marketSortBy === 'totalSupply'}
                  direction={marketSortBy === 'totalSupply' ? marketSortDirection : 'desc'}
                  onClick={() => handleMarketSort('totalSupply')}
                >
                  Total Supply
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={marketSortBy === 'priceChange24h'}
                  direction={marketSortBy === 'priceChange24h' ? marketSortDirection : 'desc'}
                  onClick={() => handleMarketSort('priceChange24h')}
                >
                  24h Change
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((crypto, index) => (
              <TableRow
                key={crypto.id}
                sx={{
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.08)' : '#f9fafb',
                  },
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                <TableCell sx={{ fontWeight: 500 }}>{crypto.rank || index + 1}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {crypto.image && (
                      <img
                        src={crypto.image}
                        alt={crypto.name}
                        style={{ width: 24, height: 24, borderRadius: '50%' }}
                      />
                    )}
                    <Typography sx={{ fontWeight: 500 }}>{crypto.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{crypto.symbol}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {crypto.coinMetricsAsset || 'N/A'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {formatCurrency(crypto.price)}
                </TableCell>
                <TableCell align="right">
                  {crypto.marketCap ? formatCurrency(crypto.marketCap) : 'N/A'}
                </TableCell>
                <TableCell align="right">
                  {crypto.volume24h ? formatCurrency(crypto.volume24h) : 'N/A'}
                </TableCell>
                <TableCell align="right">
                  {formatInteger(crypto.activeAddresses)}
                </TableCell>
                <TableCell align="right">
                  {formatInteger(crypto.transactionCount24h)}
                </TableCell>
                <TableCell align="right">
                  {formatNativeFee(crypto.networkFeesNative, crypto.symbol)}
                </TableCell>
                <TableCell align="right">
                  {crypto.circulatingSupply ? formatNumber(crypto.circulatingSupply) : 'N/A'}
                </TableCell>
                <TableCell align="right">
                  {crypto.totalSupply ? formatNumber(crypto.totalSupply) : 'N/A'}
                </TableCell>
                <TableCell align="right">{formatPercentage(crypto.priceChange24h)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={sortedMarketData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.08)' : 'transparent',
            color: 'text.primary',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              color: 'text.primary',
            },
            '& .MuiIconButton-root': {
              color: 'text.primary',
            },
            '& .MuiSelect-icon': {
              color: 'text.primary',
            },
          }}
        />
      </TableContainer>}

      {view === 'price' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 1.5, height: { xs: 360, md: 640 }, overflow: 'auto' }}>
              <Typography variant="subtitle1" sx={{ px: 1, pb: 1, fontWeight: 600 }}>
                Select Cryptocurrency
              </Typography>
              <TextField
                size="small"
                placeholder="Search coins..."
                value={priceChartSearch}
                onChange={(e) => setPriceChartSearch(e.target.value.toLowerCase())}
                fullWidth
                sx={{ mb: 1.5, px: 1 }}
                variant="outlined"
              />
              <List dense disablePadding>
                {cryptoData
                  .filter(
                    (coin) =>
                      coin.symbol.toLowerCase().includes(priceChartSearch) ||
                      coin.name.toLowerCase().includes(priceChartSearch)
                  )
                  .map((coin) => (
                    <ListItemButton
                      key={coin.id}
                      selected={coin.id === selectedPriceCoinId}
                      onClick={() => setSelectedPriceCoinId(coin.id)}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText
                        primary={`${coin.symbol} - ${coin.name}`}
                        secondary={formatCurrency(coin.price)}
                        primaryTypographyProps={{ fontWeight: coin.id === selectedPriceCoinId ? 700 : 500, fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.78rem' }}
                      />
                    </ListItemButton>
                  ))}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 2, height: { xs: 420, md: 640 } }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {selectedPriceCoin ? `${selectedPriceCoin.name} (${selectedPriceCoin.symbol})` : 'Crypto Price Chart'}
              </Typography>
              {selectedPriceCoin && (
                <CryptoPriceChart coin={selectedPriceCoin} isDarkMode={isDarkMode} height="calc(100% - 40px)" />
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {view === 'coins' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 1.5, height: { xs: 360, md: 640 }, overflow: 'auto' }}>
              <Typography variant="subtitle1" sx={{ px: 1, pb: 1, fontWeight: 600 }}>
                Select Cryptocurrency
              </Typography>
              <TextField
                size="small"
                placeholder="Search coins..."
                value={coinsSearch}
                onChange={(e) => setCoinsSearch(e.target.value.toLowerCase())}
                fullWidth
                sx={{ mb: 1.5, px: 1 }}
                variant="outlined"
              />
              <List dense disablePadding>
                {cryptoData
                  .filter(
                    (coin) =>
                      coin.symbol.toLowerCase().includes(coinsSearch) ||
                      coin.name.toLowerCase().includes(coinsSearch)
                  )
                  .map((coin) => (
                    <ListItemButton
                      key={coin.id}
                      selected={coin.id === selectedPriceCoinId}
                      onClick={() => setSelectedPriceCoinId(coin.id)}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText
                        primary={`${coin.symbol} - ${coin.name}`}
                        secondary={formatCurrency(coin.price)}
                        primaryTypographyProps={{ fontWeight: coin.id === selectedPriceCoinId ? 700 : 500, fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.78rem' }}
                      />
                    </ListItemButton>
                  ))}
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 2.5, height: { xs: 'auto', md: 'auto' }, overflow: 'auto' }}>
              {selectedPriceCoin ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    {selectedPriceCoin.image && (
                      <img
                        src={selectedPriceCoin.image}
                        alt={selectedPriceCoin.name}
                        style={{ width: 48, height: 48, borderRadius: '50%' }}
                      />
                    )}
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {selectedPriceCoin.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedPriceCoin.symbol.toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 3, p: 1.5, bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f9fafb', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
                      About {selectedPriceCoin.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                      {isDescriptionLoading
                        ? 'Loading description...'
                        : selectedCoinDescription || 'Description not available.'}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ p: 1.5, bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f9fafb', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Current Price
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                          {formatCurrency(selectedPriceCoin.price)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <Box sx={{ p: 1.5, bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f9fafb', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          24h Change
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {formatPercentage(selectedPriceCoin.priceChange24h)}
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <Box sx={{ p: 1.5, bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f9fafb', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Market Cap
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.95rem' }}>
                          {selectedPriceCoin.marketCap ? formatCurrency(selectedPriceCoin.marketCap) : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <Box sx={{ p: 1.5, bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f9fafb', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          24h Volume
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.95rem' }}>
                          {selectedPriceCoin.volume24h ? formatCurrency(selectedPriceCoin.volume24h) : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <Box sx={{ p: 1.5, bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f9fafb', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Active Addresses (24h)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.95rem' }}>
                          {selectedPriceCoin.activeAddresses !== null
                            ? formatNumber(selectedPriceCoin.activeAddresses)
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <Box sx={{ p: 1.5, bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f9fafb', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Transactions (24h)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.95rem' }}>
                          {selectedPriceCoin.transactionCount24h !== null
                            ? formatNumber(selectedPriceCoin.transactionCount24h)
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <Box sx={{ p: 1.5, bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f9fafb', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Circulating Supply
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.95rem' }}>
                          {selectedPriceCoin.circulatingSupply
                            ? formatNumber(selectedPriceCoin.circulatingSupply)
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <Box sx={{ p: 1.5, bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f9fafb', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Total Supply
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.95rem' }}>
                          {selectedPriceCoin.totalSupply
                            ? formatNumber(selectedPriceCoin.totalSupply)
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <Box sx={{ p: 1.5, bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : '#f9fafb', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Network Fees (24h)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, fontSize: '0.9rem' }}>
                          {formatNativeFee(selectedPriceCoin.networkFeesNative, selectedPriceCoin.symbol)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {selectedPriceCoin.coinMetricsAsset && (
                    <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${isDarkMode ? 'rgba(148, 163, 184, 0.2)' : '#e5e7eb'}` }}>
                      <Typography variant="caption" color="text.secondary">
                        On-chain Asset ID: <strong>{selectedPriceCoin.coinMetricsAsset}</strong>
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${isDarkMode ? 'rgba(148, 163, 184, 0.2)' : '#e5e7eb'}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Price Chart
                    </Typography>
                    <Box sx={{ height: { xs: 420, md: 560 }, borderRadius: 1, overflow: 'hidden', bgcolor: isDarkMode ? 'rgba(148, 163, 184, 0.05)' : '#fafbfc' }}>
                      <CryptoPriceChart coin={selectedPriceCoin} isDarkMode={isDarkMode} />
                    </Box>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary">
                    Select a cryptocurrency to view details
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Information Footer */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.14)' : '#f0f9ff',
          borderRadius: 1,
          borderLeft: '4px solid #3b82f6',
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          <strong>Note:</strong> Market data is fetched from CoinGecko with CoinCap as fallback, and updates every few minutes. 
          Market Cap = Price × Circulating Supply. Circulating Supply represents coins currently in circulation. 
          Total Supply includes all coins that have been created (some may be locked or burned). Active addresses, transaction count, and network fees are sourced from Coin Metrics Community API where available, using chain-specific mapped assets (for example USDT on Ethereum uses usdt_eth).
        </Typography>
      </Box>
    </Box>
  );
};

export default CryptoAnalysis;
